import { useQuery } from '@tanstack/react-query';

import { loadStateOf } from '../../../core-components/theme/components/Screen';
import type { LoadState } from '../../../core-components/theme/components/Screen';
import { profileQueryOptions } from '../../account';
import { useUpdateMySettings, mySettingsQueryOptions } from '../../close-the-loop';
import type { HandoffSaveStatus, HandoffStall } from '../../close-the-loop';
import { doneSummaryOf } from '../domain/doneSummary';
import type { DoneSummary } from '../domain/doneSummary';

export interface OnboardingDone {
  readonly load: LoadState;
  readonly summary: DoneSummary | null;
  readonly showHandoff: boolean;
  readonly handoffAlerts: boolean;
  readonly handoffDisabled: boolean;
  readonly handoffStatus: HandoffSaveStatus;
  readonly handoffStall: HandoffStall | null;
  readonly setHandoff: (next: boolean) => void;
}

const handoffStatusOf = (save: { isPending: boolean; isError: boolean; isSuccess: boolean }): HandoffSaveStatus => {
  if (save.isPending) return 'saving';
  if (save.isError) return 'failed';
  return save.isSuccess ? 'saved' : 'idle';
};

export const useOnboardingDone = (): OnboardingDone => {
  const settings = useQuery(mySettingsQueryOptions());
  const profile = useQuery(profileQueryOptions());
  const { mutate, isPending, isError, isSuccess, variables } = useUpdateMySettings();

  const server = settings.data;
  const saving = isPending ? variables?.patch.handoffAlerts : undefined;

  return {
    load: loadStateOf(settings),
    summary: server ? doneSummaryOf(server, profile.data?.number ?? null) : null,
    showHandoff: server?.receivesHandoffAlerts ?? false,
    handoffAlerts: saving ?? server?.handoffAlerts ?? true,
    handoffDisabled: !(server?.manageable ?? false),
    handoffStatus: handoffStatusOf({ isPending, isError, isSuccess }),
    handoffStall: server?.handoffStall ?? null,
    setHandoff: (next) => {
      if (isPending) return;
      mutate({ patch: { handoffAlerts: next } });
    },
  };
};
